/**
 * Accent-insensitive search for Icelandic text ("olafur" finds "Ólafur", "thor" finds "Þór")
 * and Icelandic alphabetical ordering (á after a, ð after d, þ æ ö at the end).
 */

const FOLD: Record<string, string> = {
  á: 'a',
  à: 'a',
  â: 'a',
  ä: 'a',
  å: 'a',
  é: 'e',
  è: 'e',
  ê: 'e',
  ë: 'e',
  í: 'i',
  ì: 'i',
  î: 'i',
  ï: 'i',
  ó: 'o',
  ò: 'o',
  ô: 'o',
  ö: 'o',
  ø: 'o',
  õ: 'o',
  ú: 'u',
  ù: 'u',
  û: 'u',
  ü: 'u',
  ý: 'y',
  ÿ: 'y',
  ð: 'd',
  þ: 'th',
  æ: 'ae',
  ß: 'ss',
};

const FOLD_RE = /[àáâäåèéêëìíîïòóôõöøùúûüýÿðþæß]/g;

/** Lower-case and strip Icelandic (and common Nordic/European) diacritics. */
export function foldIcelandic(s: string): string {
  return s
    .normalize('NFC')
    .toLocaleLowerCase('is')
    .replace(FOLD_RE, (ch) => FOLD[ch] ?? ch)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Icelandic alphabetical order (Intl.Collator('is')): a á b d ð e é … þ æ ö. */
export const compareIcelandic: (a: string, b: string) => number = new Intl.Collator('is').compare;

/** Split a query into folded tokens; empty query → no tokens. */
export function searchTokens(query: string): string[] {
  const f = foldIcelandic(query);
  return f ? f.split(' ') : [];
}

/**
 * True when every query token occurs in at least one of the given fields (accent-insensitive).
 * An empty query matches everything.
 */
export function matchesSearch(tokens: readonly string[], ...fields: (string | null | undefined)[]): boolean {
  if (tokens.length === 0) return true;
  const hay = foldIcelandic(fields.filter(Boolean).join(' '));
  return tokens.every((t) => hay.includes(t));
}
